/**
 * pngExport.js — the team's 15 Oct 2026 asks, both of them about getting PNGs out.
 *
 *   1. "Add an option to export the final generated output as PNG image files, allowing users to
 *      access and use individual images more conveniently."
 *   2. "Add an option to export the watermark page as a blank page with a transparent background.
 *      This allows the page to be used as a transparent overlay or customized for further use."
 *
 * 🔴 ALPHA LIVES IN THE PNG AND NEVER IN THE PDF. A page-wide alpha object makes any print path
 * that cannot composite rasterise the WHOLE sheet, which is what cost the frame its vector lines
 * on the team's own printer — measured 30 Sep 2026 with `pdftops -level2`: one 2480x3507 raster,
 * zero vector strokes. caseReportDoc.js carries the same warning on the logo, where a single
 * 16 mm soft mask was enough on its own. Nothing in this file may be fed back into the PDF path.
 */

import { asDataUrl } from '../pdfChassis.js'

/** The watermark's own colour, before `overWhite` flattens it onto the page. */
export const WATERMARK_INK = [140, 150, 155]

/**
 * How far a rasterised pixel may sit from the computed flat colour and still be the watermark.
 *
 * MEASURED, not chosen. `overWhite([140,150,155], 0.1686)` computes rgb(236,237,238), and a real
 * 300 dpi raster of the blank page carries rgb(235,236,237) — one level low on every channel,
 * from the rasteriser's own rounding. An exact match would have found nothing.
 */
export const WATERMARK_TOLERANCE = 3

/** Page millimetres -> pixel column/row, for a raster of `w` x `h` covering a 210 x 297 page. */
export const mmToPx = (mm, px, pageMm) => Math.round((mm / pageMm) * px)

/**
 * How far OUTSIDE the stated logo box its painted white still reaches, in millimetres.
 *
 * MEASURED, not chosen. Keying exactly the stated box left an 842-pixel white fringe along the
 * top and left edges, and the fringe's own bounding box ran x 76.52..133.99, y 1.61..18.12 against
 * a stated box of x 76.62..134.11, y 1.69..18.20 — i.e. the rasteriser antialiases the asset's
 * edge across the boundary, up to 0.10 mm outside it. 0.3 mm covers that with room, and is still
 * far short of anything else on the page: the nearest ink is the header rule 3.5 mm below.
 */
export const LOGO_EDGE_PAD_MM = 0.3

/**
 * The geometry's own logo box, in the shape the overlay wants.
 *
 * One place, because the chassis spells it `{x, top, width, height}` and the overlay works in
 * `{x, y, w, h}`. Two hand conversions of the same rectangle is how they come to disagree.
 */
export const logoBoxOf = (g) => (g?.logo
  ? { x: g.logo.x, y: g.logo.top, w: g.logo.width, h: g.logo.height }
  : null)

/**
 * Turn a rasterised BLANK sheet into a TRANSPARENT OVERLAY, in place.
 *
 * 🔴 A BLANK SHEET, not any page. The watermark is found by COLOUR, so anything else on the page
 * sitting within the tolerance of the flat watermark grey would be recoloured and made 17 %
 * transparent along with it. A blank sheet carries only chrome, so nothing else can match; a
 * report page carries bone-coloured renders whose pale highlights can. Do not reach for this to
 * make a report page transparent without first deciding what should happen to the renders.
 *
 * The PDF paints no page background, so a raster taken with an alpha channel already has
 * everything but the ink transparent. Two things still have to be undone, and both were found by
 * measuring a real blank page rather than by reading the writer:
 *
 *   · THE WATERMARK IS NOT TRANSPARENT INK. It is drawn as a flat light grey ON WHITE — the
 *     colour `overWhite` computes — because transparency in the PDF is the print defect above.
 *     Laid over a dark render as-is it reads as solid pale grey marks, not a watermark. Every
 *     watermark pixel is put back to its true colour at its true opacity; the glyph's own
 *     antialiasing is already in the alpha channel, so it is scaled rather than replaced.
 *   · THE LOGO PAINTS AN OPAQUE WHITE BOX. Its asset is deliberately a PNG with NO alpha channel
 *     (colour type 2), because jsPDF attaches a soft mask to a colour-type-6 PNG even when every
 *     pixel is opaque. So its white background, and the counters inside its letters, arrive
 *     opaque. Measured on the blank page: 119,017 white pixels in x 76.52..133.99 mm,
 *     y 1.61..18.12 mm — the master's logo box to within 0.1 mm. White is keyed out INSIDE THAT
 *     BOX ONLY, so nothing elsewhere on the page can be punched through by accident.
 *
 * @param {{data: Uint8ClampedArray, width: number, height: number}} img RGBA pixels, modified.
 * @param {{watermark: {color: number[], opacity: number}, logoBox: {x,y,w,h}|null,
 *          page?: {w: number, h: number}}} geometry
 * @returns {{watermarkPixels: number, logoPixels: number}} what was changed, for the caller to assert on.
 */
export function toTransparentOverlay(img, { watermark, logoBox, page = { w: 210, h: 297 } }) {
  const { data, width, height } = img
  const ink = watermark?.color ?? WATERMARK_INK
  const opacity = watermark?.opacity ?? 0
  // The flat colour the writer actually put on the page, from the same arithmetic it used.
  const flat = ink.map((c) => Math.round(255 - opacity * (255 - c)))

  let watermarkPixels = 0
  let logoPixels = 0

  const pad = LOGO_EDGE_PAD_MM
  const x0 = logoBox ? mmToPx(logoBox.x - pad, width, page.w) : -1
  const x1 = logoBox ? mmToPx(logoBox.x + logoBox.w + pad, width, page.w) : -1
  const y0 = logoBox ? mmToPx(logoBox.y - pad, height, page.h) : -1
  const y1 = logoBox ? mmToPx(logoBox.y + logoBox.h + pad, height, page.h) : -1

  for (let y = 0; y < height; y++) {
    const inLogoRow = logoBox && y >= y0 && y <= y1
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      if (data[i + 3] === 0) continue

      if (Math.abs(data[i] - flat[0]) <= WATERMARK_TOLERANCE
        && Math.abs(data[i + 1] - flat[1]) <= WATERMARK_TOLERANCE
        && Math.abs(data[i + 2] - flat[2]) <= WATERMARK_TOLERANCE) {
        data[i] = ink[0]
        data[i + 1] = ink[1]
        data[i + 2] = ink[2]
        data[i + 3] = Math.round(data[i + 3] * opacity)
        watermarkPixels += 1
        continue
      }

      if (inLogoRow && x >= x0 && x <= x1
        && data[i] > 250 && data[i + 1] > 250 && data[i + 2] > 250) {
        data[i + 3] = 0
        logoPixels += 1
      }
    }
  }
  return { watermarkPixels, logoPixels }
}

/**
 * A safe, stable file name stem for a case's exports.
 *
 * The same shape the PDF downloads already use in CaseReportBuilder.jsx, so a folder of exports
 * sorts beside the report it came from rather than under a second naming scheme.
 */
export const exportStem = (caseId) => String(caseId || 'case').replace(/[^\w-]/g, '')

/**
 * The scene renders the last build produced, as PNG entries ready to zip.
 *
 * These are the pictures themselves — no frame, no header, no watermark — which is what the team
 * reach for when they compose a page by hand. They already exist as data URLs once a report has
 * been built, so nothing is re-rendered and nothing is drawn a second way.
 *
 * `views` is the renderScenes shape: { sceneId: { VIEW: image } }. `sceneNames` maps a scene id to
 * the name the document gave it, so a file says what it holds instead of `s001`.
 *
 * 🔴 THE IMAGE IS A BARE BASE64 PAYLOAD, not a data URL. meshRenderer.js:690 returns
 * `canvas.toDataURL('image/png').split(',')[1]` — the prefix is stripped. A first version of this
 * filtered on `startsWith('data:image/')` and therefore skipped EVERY render, so the export
 * produced an empty zip and said the build had no renders. It was caught by driving the real app,
 * not by a unit test: the test had been written around the same wrong assumption. `asDataUrl` is
 * the house helper for exactly this (pdfChassis.js) and takes either shape.
 */
export function renderEntries(views, sceneNames = {}) {
  const out = []
  for (const [sceneId, byView] of Object.entries(views ?? {})) {
    const label = String(sceneNames[sceneId] ?? sceneId).replace(/[^\w-]+/g, '_').replace(/^_+|_+$/g, '')
    for (const [view, img] of Object.entries(byView ?? {})) {
      if (typeof img !== 'string' || !img) continue
      out.push({ name: `${label || sceneId}-${view}.png`, dataUrl: asDataUrl(img) })
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}

/** The bytes behind a `data:` URL. */
export function dataUrlToBytes(url) {
  // A bare base64 payload is accepted as well as a full data: URL, for the same reason
  // `renderEntries` uses `asDataUrl`: the renderer hands back the payload alone.
  const comma = url.indexOf(',')
  const b64 = comma < 0 ? url : url.slice(comma + 1)
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes
}
